/**
 * TMS Report — Driver Pay / Trip History
 * Shows per-driver trip history: from → to, timing, job info, AP cost
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, RefreshCcw, Loader2, UserCheck, MapPin, Clock, Truck, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';

type TripRow = {
  id: string;
  jobNumber: string;
  jobType: string;
  containerNo: string;
  containerSize: string;
  driverName: string;
  vehicleNo: string;
  customerName: string;
  // Route
  pickupLocation: string;
  deliveryLocation: string;
  // Timing
  pickupDate: string;
  deliveryDate: string;
  eta: string;
  // Financials
  apAmount: number;
  arAmount: number;
  // Status
  status: string;
  createdAt: string;
};

type LedgerEntry = { jobId: string; entryType: string; amount: number };

function normaliseJob(r: Record<string, unknown>): TripRow {
  const d = (r.data ?? {}) as Record<string, unknown>;
  return {
    id: String(r.id ?? ''),
    jobNumber: String(r.jobNumber ?? r.job_number ?? r.id ?? ''),
    jobType: String(r.jobType ?? r.job_type ?? d.job_type ?? '').toUpperCase(),
    containerNo: String(r.containerNo ?? r.container_no ?? d.container_no ?? ''),
    containerSize: String(r.containerSize ?? r.container_size ?? d.container_size ?? '').replace("'", ''),
    driverName: String(r.driverName ?? r.driver_name ?? d.driver_name ?? ''),
    vehicleNo: String(r.vehicleNo ?? r.vehicle_no ?? d.vehicle_no ?? d.vehicle_plate ?? ''),
    customerName: String(r.customerName ?? r.customer_name ?? d.client_code ?? d.customer ?? ''),
    pickupLocation: String(r.pickupLocation ?? r.pickup_location ?? d.port ?? d.pickup_address ?? d.origin ?? ''),
    deliveryLocation: String(r.deliveryLocation ?? r.delivery_location ?? d.delivery_address ?? d.destination ?? r.deliveryAddress ?? ''),
    pickupDate: String(r.pickupDate ?? r.pickup_date ?? d.pickup_date ?? '').slice(0, 10),
    deliveryDate: String(r.deliveryDate ?? r.delivery_date ?? d.delivery_date ?? '').slice(0, 10),
    eta: String(r.eta ?? d.eta ?? '').slice(0, 10),
    apAmount: Number(r.apAmount ?? r.ap_amount ?? 0),
    arAmount: Number(r.arAmount ?? r.ar_amount ?? 0),
    status: String(r.status ?? 'draft'),
    createdAt: String(r.createdAt ?? r.created_at ?? '').slice(0, 10),
  };
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  confirmed: 'bg-sky-100 text-sky-700',
  in_transit: 'bg-yellow-100 text-yellow-700',
  delivered: 'bg-green-100 text-green-700',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-600',
};

export default function TmsReportDriverPayPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [trips, setTrips] = useState<TripRow[]>([]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedDriver, setSelectedDriver] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 90); return d.toISOString().slice(0, 10); });
  const [dateTo, setDateTo] = useState(today);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const jobParams = new URLSearchParams({ limit: '500', sort: 'created_at', order: 'desc' });
      if (dateFrom) jobParams.set('dateFrom', dateFrom);
      if (dateTo) jobParams.set('dateTo', dateTo);
      const [jobRes, ledgerRes] = await Promise.all([
        api.get<{ items?: Record<string, unknown>[] }>(`/jobs?${jobParams}`),
        api.get<{ items?: Record<string, unknown>[] }>('/ledger-entries?limit=100').catch(() => ({ items: [] as Record<string, unknown>[] })),
      ]);
      setTrips((Array.isArray(jobRes) ? jobRes : (jobRes.items ?? [])).map(normaliseJob));
      setLedger((Array.isArray(ledgerRes) ? ledgerRes : ((ledgerRes as { items?: Record<string, unknown>[] }).items ?? [])).map((r) => ({
        jobId: String(r.jobId ?? r.job_id ?? ''),
        entryType: String(r.entryType ?? r.entry_type ?? ''),
        amount: Number(r.amount ?? 0),
      })));
    } catch { setTrips([]); setLedger([]); } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Merge AP from ledger into trip rows
  const apByJob = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of ledger) {
      const t = e.entryType.toLowerCase();
      if (t.includes('ap') || t.includes('payable') || t.includes('cost')) {
        m.set(e.jobId, (m.get(e.jobId) ?? 0) + e.amount);
      }
    }
    return m;
  }, [ledger]);

  const enriched = useMemo<TripRow[]>(() => trips.map((t) => ({
    ...t,
    apAmount: t.apAmount || (apByJob.get(t.id) ?? 0),
  })), [trips, apByJob]);

  // All distinct drivers — only those with actual jobs with a driver name
  const allDrivers = useMemo(() => {
    const s = [...new Set(enriched.map((t) => t.driverName).filter(Boolean))].sort();
    return s;
  }, [enriched]);

  // Filtered trips
  const filtered = useMemo(() => enriched.filter((t) => {
    if (!t.driverName) return false;
    if (selectedDriver !== 'all' && t.driverName !== selectedDriver) return false;
    const pivot = t.deliveryDate || t.pickupDate || t.eta || t.createdAt;
    if (dateFrom && pivot && pivot < dateFrom) return false;
    if (dateTo && pivot && pivot > dateTo) return false;
    return true;
  }), [enriched, selectedDriver, dateFrom, dateTo]);

  // Group by driver for summary line above the table
  const driverTotals = useMemo(() => {
    const m = new Map<string, { trips: number; apTotal: number; done: number }>();
    for (const t of filtered) {
      const s = m.get(t.driverName) ?? { trips: 0, apTotal: 0, done: 0 };
      s.trips++;
      s.apTotal += t.apAmount;
      if (['completed', 'delivered'].includes(t.status)) s.done++;
      m.set(t.driverName, s);
    }
    return m;
  }, [filtered]);

  const grandApTotal = filtered.reduce((s, t) => s + t.apAmount, 0);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={selectedDriver} onValueChange={setSelectedDriver}>
          <SelectTrigger className="h-8 w-[200px] text-xs">
            <UserCheck className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="All Drivers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Drivers</SelectItem>
            {allDrivers.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-[150px] text-xs" />
        <span className="text-muted-foreground text-xs">—</span>
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-[150px] text-xs" />
        <Button variant="outline" size="sm" className="h-8" onClick={fetchAll} disabled={loading}>
          <RefreshCcw className="h-3.5 w-3.5" />
        </Button>
        <Badge variant="secondary" className="text-xs">{filtered.length} trips</Badge>
        {grandApTotal > 0 && (
          <Badge className="bg-orange-100 text-orange-800 text-xs ml-auto">
            AP Total: SGD {grandApTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground text-sm">
          <DollarSign className="h-8 w-8 mx-auto mb-3 opacity-30" />
          {allDrivers.length === 0 && enriched.length > 0
            ? `No driver assignments found — ${enriched.length} job${enriched.length !== 1 ? 's' : ''} exist but none have a driver assigned. Assign drivers via the job detail page.`
            : 'No trip history found for this driver / date range.'}
        </CardContent></Card>
      ) : (
        (() => {
          // If a specific driver is selected, show flat table; else group by driver
          const driversToShow = selectedDriver !== 'all' ? [selectedDriver] : allDrivers.filter((d) => driverTotals.has(d));
          return (
            <div className="space-y-4">
              {driversToShow.map((driver) => {
                const driverTrips = filtered.filter((t) => t.driverName === driver);
                const summary = driverTotals.get(driver);
                return (
                  <Card key={driver}>
                    <CardContent className="p-0">
                      {/* Driver header */}
                      <div className="flex items-center gap-3 px-4 py-2.5 bg-muted/30 border-b">
                        <UserCheck className="h-4 w-4 text-primary shrink-0" />
                        <span className="font-semibold text-sm">{driver}</span>
                        {driverTrips[0]?.vehicleNo && (
                          <Badge variant="outline" className="text-[10px]"><Truck className="h-3 w-3 mr-1" />{driverTrips[0].vehicleNo}</Badge>
                        )}
                        <div className="flex items-center gap-3 ml-auto text-xs text-muted-foreground">
                          <span>{summary?.trips ?? 0} trip{summary?.trips !== 1 ? 's' : ''}</span>
                          <span className="text-emerald-600">{summary?.done ?? 0} completed</span>
                          {(summary?.apTotal ?? 0) > 0 && (
                            <span className="font-semibold text-orange-700">
                              AP: SGD {(summary!.apTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Trip list */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b text-muted-foreground bg-muted/10">
                              <th className="text-left py-1.5 px-3">Job No.</th>
                              <th className="text-left py-1.5 px-2">Type / Cntr</th>
                              <th className="text-left py-1.5 px-2">Customer</th>
                              <th className="text-left py-1.5 px-2">From (Pickup)</th>
                              <th className="text-left py-1.5 px-2">→ To (Delivery)</th>
                              <th className="text-left py-1.5 px-2">Pickup</th>
                              <th className="text-left py-1.5 px-2">Delivery</th>
                              <th className="text-left py-1.5 px-2">Status</th>
                              <th className="text-right py-1.5 px-3">AP (SGD)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {driverTrips.map((t) => (
                              <tr key={t.id} className="border-b last:border-0 hover:bg-muted/20 align-top">
                                <td className="py-2 px-3">
                                  <Link to={`/hms/jobs/${t.id}`} className="font-mono text-primary hover:underline flex items-center gap-0.5">
                                    {t.jobNumber}<ChevronRight className="h-3 w-3 shrink-0" />
                                  </Link>
                                </td>
                                <td className="py-2 px-2">
                                  <div className="font-medium">{t.jobType || '—'}</div>
                                  {t.containerNo && <div className="font-mono text-[10px] text-muted-foreground">{t.containerNo}{t.containerSize ? ` (${t.containerSize})` : ''}</div>}
                                </td>
                                <td className="py-2 px-2 max-w-[110px] truncate">{t.customerName || '—'}</td>
                                <td className="py-2 px-2">
                                  <div className="flex items-start gap-1">
                                    <MapPin className="h-3 w-3 text-blue-500 mt-0.5 shrink-0" />
                                    <span className="line-clamp-2 text-[11px]">{t.pickupLocation || '—'}</span>
                                  </div>
                                </td>
                                <td className="py-2 px-2">
                                  <div className="flex items-start gap-1">
                                    <MapPin className="h-3 w-3 text-green-500 mt-0.5 shrink-0" />
                                    <span className="line-clamp-2 text-[11px]">{t.deliveryLocation || '—'}</span>
                                  </div>
                                </td>
                                <td className="py-2 px-2 whitespace-nowrap">
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <Clock className="h-3 w-3 shrink-0" />{t.pickupDate || '—'}
                                  </div>
                                </td>
                                <td className="py-2 px-2 whitespace-nowrap">
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <Clock className="h-3 w-3 shrink-0" />{t.deliveryDate || t.eta || '—'}
                                  </div>
                                </td>
                                <td className="py-2 px-2">
                                  <Badge className={`text-[10px] px-1.5 ${STATUS_COLORS[t.status] ?? 'bg-gray-100'}`}>{t.status.replace(/_/g, ' ')}</Badge>
                                </td>
                                <td className="py-2 px-3 text-right font-mono font-semibold">
                                  {t.apAmount > 0 ? t.apAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          );
        })()
      )}
    </div>
  );
}
